import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistProfile,
  selectTherapistName,
  selectCanCreateGreenInvoice,
  selectIsDigitalInvoiceActive,
  selectClinicProfile,
  selectCanAccessFinance,
  selectIsAdmin,
  selectIsNutritionClinic,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { 
  useGetAppointmentsQuery,
  useGetPendingActionAppointmentsQuery,
  useUpdateAppointmentMutation,
  calendarApi,
} from "../../store/services/calendarApi";
import {
  useGetTasksQuery,
  useGetRemindersQuery,
  useCreateTaskMutation,
} from "../../store/services/tasksApi";
import {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
} from "../../store/services/notificationsApi";
import { useGetLeadsQuery } from "../../store/services/leadsApi";
import { useUpdateTherapistMutation } from "../../store/services/therapistApi";
import { isFeatureEnabled } from "../../config/featureFlags";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import HoverTip from "../ui/HoverTip";
import PendingActionsDemo from "./pendingDemo/PendingActionsDemo";
import NutritionPendingDemo from "./pendingDemo/NutritionPendingDemo";
import EmptyDraw from "../ui/EmptyDraw";
import NutritionPendingSection from "./NutritionPendingSection";
import {
  useGetActiveLiveSessionsForTherapistQuery,
  useEndLiveSessionMutation,
  liveSessionsApi,
} from "../../store/services/liveSessionsApi";
import {
  useUpdateTransactionMutation,
  useUpdateTransactionStatusMutation,
  financeApi,
} from "../../store/services/financeApi";
import { 
  resolveLeadStatuses,
  findLeadStatus,
  getTransactionByAppointment,
  TRANSACTION_STATUS,
} from "../../firebase";
import { DEFAULT_POLLING_INTERVAL, useVisibilityRefresh } from "../../hooks/useRealtimeQuery";
import InvoiceQuickModal from "../morning/InvoiceQuickModal";
import DigitalInvoiceQuickModal from "../digitalInvoice/DigitalInvoiceQuickModal";
import {
  Bell,
  CalendarCheck,
  Calendar,
  ListChecks,
  Note,
  UsersThree,
  Check,
  Clock,
  Hourglass,
  CircleDashed,
  Eye,
  EyeSlash,
  Plus,
  ArrowRight,
  CheckCircle,
  ClockCountdown,
  X,
  Lightning,
  NotePencil,
  CurrencyCircleDollar,
  Warning,
  Receipt,
  Play,
  Stop,
  CaretDown,
  ArrowsOutSimple,
  PlayCircle,
} from "@phosphor-icons/react";
import { HelpButton } from "../shared";
import PendingFocusMode from "./PendingFocusMode";
import DatePicker from "../ui/DatePicker";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./CollapsibleSections.css";
import { getIsraelDateString } from "../../utils/dateUtils";

// Task status configuration
const TASK_STATUSES = [
  { value: "todo", label: "פתוח", icon: CircleDashed, color: "#4dd9b4" },
  { value: "in_progress", label: "בתהליך", icon: Hourglass, color: "#3b82f6" },
  { value: "waiting", label: "ממתין", icon: Clock, color: "#f59e0b" },
  { value: "done", label: "הסתיים", icon: Check, color: "#22c55e" },
];

const STATUS_SORT_ORDER = {
  in_progress: 0,
  waiting: 1,
  todo: 2,
  done: 3,
};

// Pending Actions Steps
const STEPS = {
  APPROVAL: "approval",
  APPROVAL_CANCEL_CONFIRM: "approval_cancel_confirm",
  CANCELLATION_PAYMENT_ASK: "cancellation_payment_ask",
  CANCELLATION_PAYMENT_AMOUNT: "cancellation_payment_amount",
  CANCELLATION_PAYMENT_DETAILS: "cancellation_payment_details",
  CANCELLATION_PAYMENT_INVOICE: "cancellation_payment_invoice",
  PAYMENT: "payment",
  PAYMENT_DETAILS: "payment_details",
  PAYMENT_INVOICE: "payment_invoice",
  PAYMENT_INVOICE_CHOICE: "payment_invoice_choice",
  PAYMENT_INVOICE_MANUAL: "payment_invoice_manual",
  PAYMENT_TASK: "payment_task",
  SUMMARY: "summary",
  SUMMARY_REMINDER: "summary_reminder",
};

const PAYMENT_METHODS = [
  { value: "cash", label: "מזומן" },
  { value: "credit", label: "אשראי" },
  { value: "transfer", label: "העברה" },
  { value: "bit", label: "אפליקציה", subLabel: "ביט/פייבוקס/אחר" },
  { value: "check", label: "צ׳ק" },
  { value: "other", label: "אחר" },
];

// Stage configuration for the pending actions list
const STAGE_CONFIG = {
  approval: {
    key: "approval",
    title: "אישור פגישה",
    shortTitle: "אישור",
    Icon: Clock,
    color: "#f59e0b",
  },
  summary: {
    key: "summary",
    title: "סיכום טיפול",
    shortTitle: "סיכום",
    Icon: NotePencil,
    color: "#8b5cf6",
  },
  payment: {
    key: "payment",
    title: "תשלום",
    shortTitle: "תשלום",
    Icon: CurrencyCircleDollar,
    color: "#14b8a6",
  },
};
const STAGE_ORDER = ["approval", "summary", "payment"];

// Sub-steps that belong to each stage
const STAGE_SUBSTEPS = {
  approval: [
    "approval",
    "approval_cancel_confirm",
    "cancellation_payment_ask",
    "cancellation_payment_amount",
    "cancellation_payment_details",
    "cancellation_payment_invoice",
  ],
  summary: ["summary", "summary_reminder"],
  payment: ["payment", "payment_details", "payment_task", "payment_invoice", "payment_invoice_choice", "payment_invoice_manual"],
};

// Section configuration — shortLabel shows on collapsed strips / mobile chips
const SECTIONS_CONFIG = [
  { id: "pendingActions", label: "פעולות ממתינות", shortLabel: "ממתינות", icon: Lightning },
  { id: "notifications", label: "התראות", shortLabel: "התראות", icon: Bell },
  { id: "calendar", label: "יומן", shortLabel: "יומן", icon: CalendarCheck },
  { id: "tasks", label: "משימות", shortLabel: "משימות", icon: ListChecks },
  { id: "reminders", label: "תזכורות", shortLabel: "תזכורות", icon: Note },
  { id: "leads", label: "מתעניינים", shortLabel: "מתעניינים", icon: UsersThree },
];

const MOBILE_BREAKPOINT = 768;

function CollapsibleSections({ onEditAppointment }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistProfile = useSelector(selectTherapistProfile);
  const therapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);
  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);
  const isDigitalInvoiceActive = useSelector(selectIsDigitalInvoiceActive);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const isAdmin = useSelector(selectIsAdmin);
  // treatments.write === false hides the "write summary" CTA (legacy therapists resolve TRUE)
  const canWriteTreatments = useSelector(selectHasPermission("treatments.write"));
  // Nutrition clinics replace the appointment-stage queue entirely; therapy
  // clinics must stay byte-identical, so this gates the query as well as the UI.
  const isNutritionClinic = useSelector(selectIsNutritionClinic);

  const allowFinanceVal = clinicProfile?.therapistPermissions?.allowFinanceAccess;
  const financeAccessRestricted = !canAccessFinance || (isAdmin && (allowFinanceVal === false || allowFinanceVal === null));

  // Active section state
  const [activeSection, setActiveSection] = useState("pendingActions");

  // Mobile layout switch (chip row + panel instead of expanding strips)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const chipRefs = useRef({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Focus mode is desktop-only — close it if the viewport shrinks
  useEffect(() => {
    if (isMobile) setShowFocusMode(false);
  }, [isMobile]);

  // Keep the active chip centered in the scrollable row on mobile
  useEffect(() => {
    if (!isMobile) return;
    const chip = chipRefs.current[activeSection];
    if (chip?.scrollIntoView) {
      chip.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    }
  }, [activeSection, isMobile]);

  // Notifications peek: in-place "show more" (no notifications page exists)
  const [showMoreNotifications, setShowMoreNotifications] = useState(false);

  // Pending actions card states
  const [cardStates, setCardStates] = useState({});
  const [completingCards, setCompletingCards] = useState({});
  const [endingLiveSessions, setEndingLiveSessions] = useState({});
  const [expandedItemId, setExpandedItemId] = useState(null);
  const allDoneShownRef = useRef(new Set());

  // "תצוגה ייעודית" fullscreen focus mode (desktop only)
  const [showFocusMode, setShowFocusMode] = useState(false);

  // ---- Pending-actions demo (client-only sample meeting; feature-flagged) ----
  const demoEnabled = isFeatureEnabled("PENDING_ACTIONS_DEMO");
  const [demoActive, setDemoActive] = useState(false);
  const [updateTherapist] = useUpdateTherapistMutation();
  const demoSeenKey = therapistId ? `clinikit_pending_demo_seen_${therapistId}` : null;
  const [demoSeenLocal, setDemoSeenLocal] = useState(() => {
    try {
      return demoSeenKey ? localStorage.getItem(demoSeenKey) === "true" : false;
    } catch {
      return false;
    }
  });
  const demoSeen = therapistProfile?.pendingDemoSeen === true || demoSeenLocal;

  const startDemo = useCallback(() => {
    setExpandedItemId(null);
    setDemoActive(true);
    trackFeature(FEATURE_KEYS.PENDING_ACTIONS_DEMO);
  }, []);

  const closeDemo = useCallback(() => {
    setDemoActive(false);
    if (!therapistId) return;
    try {
      if (demoSeenKey) localStorage.setItem(demoSeenKey, "true");
    } catch {
      /* storage unavailable — profile flag below still persists */
    }
    setDemoSeenLocal(true);
    if (therapistProfile?.pendingDemoSeen !== true) {
      updateTherapist({ uid: therapistId, data: { pendingDemoSeen: true } }).catch((error) => {
        console.error("[CK-3421] Failed to persist pendingDemoSeen", {
          errorCode: error?.code,
          errorMessage: error?.message,
          timestamp: new Date().toISOString(),
        });
      });
    }
  }, [therapistId, demoSeenKey, therapistProfile?.pendingDemoSeen, updateTherapist]);

  const renderDemoTrigger = () =>
    demoEnabled ? (
      <HoverTip text={demoActive ? "יציאה מההדגמה" : demoSeen ? "הדגמה חוזרת: איך זה עובד" : "הדגמה: איך זה עובד"}>
        <button
          type="button"
          className={`pad-tab-btn ${demoActive ? "active" : ""}`}
          aria-label={demoActive ? "יציאה מההדגמה" : "הדגמה: איך עובדות פעולות ממתינות"}
          aria-pressed={demoActive}
          onClick={(e) => {
            e.stopPropagation();
            if (demoActive) closeDemo();
            else startDemo();
          }}
        >
          <PlayCircle size={16} weight={demoActive ? "fill" : "bold"} />
        </button>
      </HoverTip>
    ) : null;

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceAppointment, setInvoiceAppointment] = useState(null);
  const [invoicePaidAmount, setInvoicePaidAmount] = useState(null);

  // Hidden reminders state
  const [hiddenReminderIds, setHiddenReminderIds] = useState(() => {
    try {
      const stored = localStorage.getItem(`clinikit_hidden_reminders_${therapistId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [showHiddenReminders, setShowHiddenReminders] = useState(false);

  // Mutations
  const [markAsRead] = useMarkAsReadMutation();
  const [updateAppointment] = useUpdateAppointmentMutation();
  const [createTask] = useCreateTaskMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [updateTransactionStatus] = useUpdateTransactionStatusMutation();
  const [endLiveSession] = useEndLiveSessionMutation();

  // ==========================================
  // DATE RANGES
  // ==========================================

  const todayRange = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    return { start, end };
  }, []);

  const upcomingRange = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const end = new Date(tomorrow);
    end.setDate(end.getDate() + 7);
    end.setHours(23, 59, 59, 999);
    return { start: tomorrow, end };
  }, []);

  // ==========================================
  // DATA FETCHING
  // ==========================================

  // Pending Actions
  const { data: pendingAppointments = [], isLoading: isLoadingPending, refetch: refetchPending } = 
    useGetPendingActionAppointmentsQuery(
      { clinicId, therapistId, isAdmin, financeAccessRestricted },
      {
        skip: !clinicId || !therapistId || isNutritionClinic,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      }
    );

  const { data: activeLiveSessions = [], isLoading: isLoadingLive, refetch: refetchLive } = 
    useGetActiveLiveSessionsForTherapistQuery(
      { clinicId, therapistId },
      { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
    );

  useVisibilityRefresh(refetchPending);
  useVisibilityRefresh(refetchLive);

  const { data: todayAppointments = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId,
      startDate: todayRange.start.toISOString(),
      endDate: todayRange.end.toISOString(),
    },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: upcomingAppointments = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId,
      startDate: upcomingRange.start.toISOString(),
      endDate: upcomingRange.end.toISOString(),
    },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: notificationsData } = useGetNotificationsQuery(
    { clinicId, therapistId, pageSize: 20 },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: personalTasks = [] } = useGetTasksQuery(
    { clinicId, scope: "personal", therapistId },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: clinicTasks = [] } = useGetTasksQuery(
    { clinicId, scope: "clinic" },
    { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: personalReminders = [] } = useGetRemindersQuery(
    { clinicId, scope: "personal", therapistId },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: clinicReminders = [] } = useGetRemindersQuery(
    { clinicId, scope: "clinic" },
    { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  // Leads v2: clinic-wide list, one cache key app-wide ({ clinicId }); gated by leads.access
  const canManageLeadsToggle = useSelector(selectHasPermission("leads.access"));
  const canAccessLeads = isAdmin || canManageLeadsToggle; // Leads v2: clinic toggle, admins always
  const sectionsConfig = useMemo(
    () => SECTIONS_CONFIG.filter((sec) => sec.id !== "leads" || canAccessLeads),
    [canAccessLeads]
  );
  const { data: leads = [] } = useGetLeadsQuery(
    { clinicId },
    { skip: !clinicId || !canAccessLeads, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  // ==========================================
  // PENDING ACTIONS HELPERS (must be before computed values)
  // ==========================================

  const getCardState = (id) => cardStates[id] || {
    currentStep: null,
    paymentAmount: null,
    paymentMethod: "cash",
    paymentDate: null,
    isLoading: false,
  };

  const getCurrentStep = (apt, cardState) => {
    if (cardState?.currentStep === STEPS.APPROVAL_CANCEL_CONFIRM) return STEPS.APPROVAL_CANCEL_CONFIRM;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_ASK) return STEPS.CANCELLATION_PAYMENT_ASK;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_AMOUNT) return STEPS.CANCELLATION_PAYMENT_AMOUNT;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_DETAILS) return STEPS.CANCELLATION_PAYMENT_DETAILS;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_INVOICE) return STEPS.CANCELLATION_PAYMENT_INVOICE;
    if (cardState?.currentStep === STEPS.PAYMENT_DETAILS) return canAccessFinance ? STEPS.PAYMENT_DETAILS : null;
    if (cardState?.currentStep === STEPS.PAYMENT_TASK) return canAccessFinance ? STEPS.PAYMENT_TASK : null;
    if (cardState?.currentStep === STEPS.PAYMENT_INVOICE) return canAccessFinance ? STEPS.PAYMENT_INVOICE : null;
    if (cardState?.currentStep === STEPS.PAYMENT_INVOICE_CHOICE) return canAccessFinance ? STEPS.PAYMENT_INVOICE_CHOICE : null;
    if (cardState?.currentStep === STEPS.PAYMENT_INVOICE_MANUAL) return canAccessFinance ? STEPS.PAYMENT_INVOICE_MANUAL : null;
    if (cardState?.currentStep === STEPS.SUMMARY_REMINDER) return STEPS.SUMMARY_REMINDER;

    if (cardState?.currentStep === STEPS.APPROVAL && apt.NotifyAboutThisMeetingApproval === true) return STEPS.APPROVAL;
    if (cardState?.currentStep === STEPS.SUMMARY && apt.NotifyAboutThisMeetingSummarize === true) return STEPS.SUMMARY;
    if (cardState?.currentStep === STEPS.PAYMENT && apt.NotifyAboutThisMeetingPaymentStatus === true) return canAccessFinance ? STEPS.PAYMENT : null;

    if (apt.NotifyAboutThisMeetingApproval === true) return STEPS.APPROVAL;
    if (apt.NotifyAboutThisMeetingSummarize === true) return STEPS.SUMMARY;
    if (apt.NotifyAboutThisMeetingPaymentStatus === true) return canAccessFinance ? STEPS.PAYMENT : null;

    return null;
  };

  // ==========================================
  // COMPUTED VALUES
  // ==========================================

  // Pending actions combined items
  const pendingItems = useMemo(() => {
    const liveSessionIds = new Set(activeLiveSessions.map((s) => s.appointmentId));
    const filteredAppointments = pendingAppointments.filter((apt) => {
      if (liveSessionIds.has(apt.id)) return false;
      const step = getCurrentStep(apt, getCardState(apt.id));
      if (step === null) return false;
      return true;
    });

    return [
      ...activeLiveSessions.map((s) => ({ type: "live", data: s, time: s.appointmentStartTime || s.startedAt })),
      ...filteredAppointments.map((apt) => ({ type: "appointment", data: apt, time: apt.startTime })),
    ].sort((a, b) => new Date(a.time) - new Date(b.time));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAppointments, activeLiveSessions, canAccessFinance, cardStates]);

  const notifications = (notificationsData?.notifications || []).filter(
    (n) => !n.readBy?.includes(therapistId)
  );

  const allTasks = useMemo(() => {
    const myClinicTasks = clinicTasks.filter((task) => task.assignedTo === therapistId);
    const combined = [...personalTasks, ...myClinicTasks];
    return combined.sort((a, b) => {
      const orderA = STATUS_SORT_ORDER[a.status] ?? 99;
      const orderB = STATUS_SORT_ORDER[b.status] ?? 99;
      return orderA - orderB;
    });
  }, [personalTasks, clinicTasks, therapistId]);

  const openTasksCount = allTasks.filter((t) => t.status !== "done").length;

  const allReminders = useMemo(() => {
    const myClinicReminders = clinicReminders.filter(
      (reminder) => reminder.createdBy === therapistId
    );
    return [...personalReminders, ...myClinicReminders].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [personalReminders, clinicReminders, therapistId]);

  const visibleReminders = useMemo(() => {
    if (showHiddenReminders) return allReminders;
    return allReminders.filter((r) => !hiddenReminderIds.includes(r.id));
  }, [allReminders, hiddenReminderIds, showHiddenReminders]);

  const hiddenRemindersCount = useMemo(() => {
    return allReminders.filter((r) => hiddenReminderIds.includes(r.id)).length;
  }, [allReminders, hiddenReminderIds]);

  const leadStatuses = useMemo(
    () => resolveLeadStatuses(clinicProfile, therapistProfile),
    [clinicProfile, therapistProfile]
  );

  const activeLeads = useMemo(() => {
    return leads
      .filter((lead) => lead.isDeleted !== true && lead.status !== "converted" && lead.status !== "lost" && !lead.convertedToPatientId)
      .sort((a, b) => {
        const statusA = leadStatuses.find((s) => s.value === a.status);
        const statusB = leadStatuses.find((s) => s.value === b.status);
        return (statusA?.order ?? 99) - (statusB?.order ?? 99);
      });
  }, [leads, leadStatuses]);

  const sortedTodayAppointments = useMemo(() => {
    return [...todayAppointments].sort(
      (a, b) => new Date(a.startTime) - new Date(b.startTime)
    );
  }, [todayAppointments]);

  const groupedUpcoming = useMemo(() => {
    const groups = {};
    [...upcomingAppointments]
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .forEach((apt) => {
        const dateKey = new Date(apt.startTime).toLocaleDateString("he-IL");
        if (!groups[dateKey]) {
          groups[dateKey] = {
            dateLabel: formatDate(apt.startTime),
            appointments: [],
          };
        }
        groups[dateKey].appointments.push(apt);
      });
    return Object.values(groups).slice(0, 3);
  }, [upcomingAppointments]);

  // Get badge counts for each section
  const sectionBadges = useMemo(() => ({
    pendingActions: pendingItems.length,
    notifications: notifications.length,
    calendar: todayAppointments.length,
    tasks: openTasksCount,
    reminders: visibleReminders.length,
    leads: activeLeads.length,
  }), [pendingItems.length, notifications.length, todayAppointments.length, openTasksCount, visibleReminders.length, activeLeads.length]);

  // ==========================================
  // HELPERS
  // ==========================================

  function formatTime(date) {
    return new Date(date).toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function formatDate(date) {
    return new Date(date).toLocaleDateString("he-IL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  }

  function formatRelativeTime(dateStr) {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `לפני ${diffMins} דק׳`;
    if (diffHours < 24) return `לפני ${diffHours} שע׳`;
    if (diffDays === 1) return "אתמול";
    if (diffDays < 7) return `לפני ${diffDays} ימים`;
    return date.toLocaleDateString("he-IL", { day: "numeric", month: "short" });
  }

  function formatAmount(amount) {
    if (!amount && amount !== 0) return null;
    return `₪${amount.toLocaleString("he-IL")}`;
  }

  function getConfirmationStatus(apt) {
    if (apt.status === "cancelled") return { text: "בוטלה", type: "cancelled" };
    if (apt.whatsappInitialDeclined === true || apt.whatsappReminderDeclined === true) return { text: "נדחתה", type: "declined" };
    if (apt.whatsappInitialConfirmed === true && apt.whatsappReminderConfirmed === true) return { text: "אושרה", type: "confirmed" };
    if (apt.whatsappReminderConfirmed === true) return { text: "אישור תזכורת", type: "reminder" };
    if (apt.whatsappInitialConfirmed === true) return { text: "אישור ראשוני", type: "initial" };
    return { text: "ממתין לאישור", type: "pending" };
  }

  function getTaskStatusConfig(status) {
    return TASK_STATUSES.find((s) => s.value === status) || TASK_STATUSES[0];
  }

  function getLeadStatusConfig(statusValue) {
    return findLeadStatus(leadStatuses, statusValue);
  }

  function getUrgencyClass(urgency) {
    if (urgency === 3) return "cs-urgency-high";
    if (urgency === 2) return "cs-urgency-medium";
    return "cs-urgency-low";
  }

  const updateCardState = (id, updates) => {
    setCardStates((prev) => ({ ...prev, [id]: { ...getCardState(id), ...updates } }));
  };

  const triggerCardCompletion = (id, onComplete) => {
    setCompletingCards((prev) => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setCompletingCards((prev) => { const next = { ...prev }; delete next[id]; return next; });
      if (onComplete) onComplete();
    }, 800);
  };

  const invalidateAllFinanceTags = (patientId) => {
    dispatch(calendarApi.util.invalidateTags([
      { type: "Finance", id: "STATS" },
      { type: "Finance", id: "PENDING_PAYMENTS" },
      ...(patientId ? [{ type: "Finance", id: `PATIENT_${patientId}` }] : []),
    ]));
    dispatch(financeApi.util.invalidateTags([
      { type: "TransactionStats", id: "MONTHLY" },
      { type: "PendingCollection", id: "LIST" },
      ...(patientId ? [{ type: "Transactions", id: `PATIENT_${patientId}` }] : []),
    ]));
  };

  // ==========================================
  // PENDING ACTIONS HANDLERS
  // ==========================================

  const handleApprovalYes = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "approval" });
    try {
      await updateAppointment({
        clinicId, appointmentId: apt.id,
        appointmentData: { isApprovedByTherapist: true, NotifyAboutThisMeetingApproval: false },
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();
      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId, transactionId: transaction.id,
            updateData: { status: TRANSACTION_STATUS.PENDING_COLLECTION },
            therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (e) { console.error("[CS-PA-001]", e); }
      updateCardState(apt.id, { isLoading: false });
    } catch (e) { console.error("[CS-PA-002]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handleApprovalNo = (apt) => updateCardState(apt.id, { currentStep: STEPS.APPROVAL_CANCEL_CONFIRM });

  const handleApprovalCancelConfirm = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "approval" });
    try {
      await updateAppointment({
        clinicId, appointmentId: apt.id,
        appointmentData: {
          status: "cancelled", isApprovedByTherapist: true,
          NotifyAboutThisMeetingApproval: false, NotifyAboutThisMeetingPaymentStatus: false, NotifyAboutThisMeetingSummarize: false,
        },
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();
      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId, transactionId: transaction.id,
            updateData: { status: TRANSACTION_STATUS.CANCELLED },
            therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (e) { console.error("[CS-PA-003]", e); }
      updateCardState(apt.id, { isLoading: false, currentStep: STEPS.CANCELLATION_PAYMENT_ASK });
    } catch (e) { console.error("[CS-PA-004]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handleApprovalCancelBack = (apt) => updateCardState(apt.id, { currentStep: null });

  const handleCancellationPaymentYes = (apt) => {
    updateCardState(apt.id, { currentStep: STEPS.CANCELLATION_PAYMENT_AMOUNT, paymentAmount: apt.paymentAmount ?? "" });
  };

  const handleCancellationPaymentNo = (apt) => triggerCardCompletion(apt.id, () => {});

  const handleCancellationPaymentAmountConfirm = (apt) => {
    const appointmentDate = new Date(apt.startTime);
    updateCardState(apt.id, {
      currentStep: STEPS.CANCELLATION_PAYMENT_DETAILS,
      paymentDate: getIsraelDateString(appointmentDate),
      paymentMethod: "cash",
    });
  };

  const handleCancellationPaymentDetailsConfirm = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "approval" });
    const cardState = getCardState(apt.id);
    const paymentAmount = cardState.paymentAmount ? parseFloat(cardState.paymentAmount) : apt.paymentAmount;
    const paymentMethod = cardState.paymentMethod || "cash";
    const paymentDate = cardState.paymentDate ? new Date(cardState.paymentDate).toISOString() : new Date().toISOString();

    try {
      await updateAppointment({
        clinicId, appointmentId: apt.id,
        appointmentData: {
          isTherapistUpdatedPaymentStatus: true, paymentStatus: "paid", paymentAmount, actualPaidAmount: paymentAmount,
          paymentMethod, paymentDate, cancellationFeeRecorded: true,
        },
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId, transactionId: transaction.id,
            updateData: { status: TRANSACTION_STATUS.PAID, amount: paymentAmount, paymentMethod, actualPaymentDate: paymentDate, isCancellationFee: true },
            therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (e) { console.error("[CS-PA-005]", e); }

      if (isUsingGreenInvoice) {
        updateCardState(apt.id, { isLoading: false, currentStep: STEPS.CANCELLATION_PAYMENT_INVOICE, paymentAmount, paymentMethod });
      } else {
        updateCardState(apt.id, { isLoading: false });
        triggerCardCompletion(apt.id, () => {});
      }
    } catch (e) { console.error("[CS-PA-006]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handleCancellationInvoiceYes = (apt) => {
    const cardState = getCardState(apt.id);
    setInvoiceAppointment({ ...apt, paymentMethod: cardState.paymentMethod ?? apt.paymentMethod ?? "cash" });
    setInvoicePaidAmount(cardState.paymentAmount ?? apt.actualPaidAmount);
    setShowInvoiceModal(true);
    updateCardState(apt.id, { currentStep: null });
    triggerCardCompletion(apt.id, () => {});
  };

  const handleCancellationInvoiceNo = (apt) => {
    updateCardState(apt.id, { currentStep: null });
    triggerCardCompletion(apt.id, () => {});
  };

  const handlePaymentYes = async (apt, amount, method, date) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "payment" });
    const paymentAmount = amount ? parseFloat(amount) : apt.paymentAmount;
    const paymentMethod = method || "cash";
    const paymentDate = date ? new Date(date).toISOString() : new Date().toISOString();

    try {
      await updateAppointment({
        clinicId, appointmentId: apt.id,
        appointmentData: {
          isTherapistUpdatedPaymentStatus: true, NotifyAboutThisMeetingPaymentStatus: false,
          paymentStatus: "paid", paymentAmount, actualPaidAmount: paymentAmount, paymentMethod, paymentDate,
        },
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId, transactionId: transaction.id,
            updateData: { status: TRANSACTION_STATUS.PAID, amount: paymentAmount, paymentMethod, actualPaymentDate: paymentDate },
            therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (e) { console.error("[CS-PA-007]", e); }

      // Always ask about receipt/invoice after payment (even without a digital
      // invoice integration — manual marking still records an invoice number/date).
      updateCardState(apt.id, { isLoading: false, currentStep: STEPS.PAYMENT_INVOICE, paymentAmount, paymentMethod });
    } catch (e) { console.error("[CS-PA-008]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handlePaymentNo = (apt) => {
    const cardState = getCardState(apt.id);
    updateCardState(apt.id, { currentStep: STEPS.PAYMENT_TASK, paymentAmount: cardState.paymentAmount ?? apt.paymentAmount });
  };

  const handlePaymentTaskYes = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "payment" });
    const cardState = getCardState(apt.id);
    const enteredAmount = cardState.paymentAmount ?? apt.paymentAmount;
    const paymentAmount = enteredAmount ? parseFloat(enteredAmount) : null;

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 6);

    const appointmentDate = new Date(apt.startTime);
    const dateStr = appointmentDate.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
    const amountStr = paymentAmount ? `₪${paymentAmount.toLocaleString("he-IL")}` : "";
    const taskTitle = paymentAmount
      ? `לגבות תשלום ${amountStr} מ${apt.patientName} (פגישה מתאריך ${dateStr})`
      : `לגבות תשלום מ${apt.patientName} (פגישה מתאריך ${dateStr})`;

    try {
      await createTask({
        clinicId,
        taskData: {
          title: taskTitle,
          description: `תזכורת לגביית תשלום${amountStr ? ` בסך ${amountStr}` : ""} מ${apt.patientName}`,
          dueDate: dueDate.toISOString(), status: "todo", priority: "medium", scope: "personal",
          assignedTo: therapistId, relatedPatientId: apt.patientId, relatedAppointmentId: apt.id,
        },
        creatorId: therapistId, creatorName: therapistName,
      }).unwrap();

      const updateData = { isTherapistUpdatedPaymentStatus: true, NotifyAboutThisMeetingPaymentStatus: false, paymentStatus: "debt" };
      if (paymentAmount !== null) updateData.paymentAmount = paymentAmount;

      await updateAppointment({
        clinicId, appointmentId: apt.id, appointmentData: updateData,
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();

      if (paymentAmount !== null) {
        try {
          const transaction = await getTransactionByAppointment(clinicId, apt.id);
          if (transaction) {
            await updateTransaction({
              clinicId, transactionId: transaction.id,
              updateData: { amount: paymentAmount, status: TRANSACTION_STATUS.PENDING_COLLECTION },
              therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
            }).unwrap();
            invalidateAllFinanceTags(apt.patientId);
          }
        } catch (e) { console.error("[CS-PA-009]", e); }
      }

      updateCardState(apt.id, { isLoading: false, currentStep: null });
    } catch (e) { console.error("[CS-PA-010]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handlePaymentTaskNo = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "payment" });
    const cardState = getCardState(apt.id);
    const enteredAmount = cardState.paymentAmount ?? apt.paymentAmount;
    const paymentAmount = enteredAmount ? parseFloat(enteredAmount) : null;

    try {
      const updateData = {
        isTherapistUpdatedPaymentStatus: true, NotifyAboutThisMeetingPaymentStatus: false,
        paymentStatus: paymentAmount !== null ? "debt" : "skipped",
      };
      if (paymentAmount !== null) updateData.paymentAmount = paymentAmount;

      await updateAppointment({
        clinicId, appointmentId: apt.id, appointmentData: updateData,
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();

      if (paymentAmount !== null) {
        try {
          const transaction = await getTransactionByAppointment(clinicId, apt.id);
          if (transaction) {
            await updateTransaction({
              clinicId, transactionId: transaction.id,
              updateData: { amount: paymentAmount, status: TRANSACTION_STATUS.PENDING_COLLECTION },
              therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
            }).unwrap();
            invalidateAllFinanceTags(apt.patientId);
          }
        } catch (e) { console.error("[CS-PA-011]", e); }
      }

      updateCardState(apt.id, { isLoading: false, currentStep: null });
    } catch (e) { console.error("[CS-PA-012]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  // "להפיק חשבונית?" → yes. When a digital invoice service is connected, ask
  // how (auto via the service, or manual). Otherwise go straight to manual entry.
  const handleReceiptYes = (apt) => {
    if (isUsingGreenInvoice) {
      updateCardState(apt.id, { currentStep: STEPS.PAYMENT_INVOICE_CHOICE });
    } else {
      updateCardState(apt.id, { currentStep: STEPS.PAYMENT_INVOICE_MANUAL, invoiceNumber: "", invoiceDate: "" });
    }
  };

  const handleInvoiceChoiceManual = (apt) =>
    updateCardState(apt.id, { currentStep: STEPS.PAYMENT_INVOICE_MANUAL, invoiceNumber: "", invoiceDate: "" });

  // Confirm a manual invoice — invoice number + date are both optional.
  const handleManualInvoiceConfirm = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "payment" });
    const cardState = getCardState(apt.id);
    const invoiceNumber = (cardState.invoiceNumber || "").trim();
    const invoiceDate = cardState.invoiceDate || "";

    try {
      const transaction = await getTransactionByAppointment(clinicId, apt.id);
      if (transaction) {
        await updateTransactionStatus({
          clinicId, transactionId: transaction.id, newStatus: TRANSACTION_STATUS.INVOICE_MANUAL,
          additionalData: {
            ...(invoiceNumber ? { invoiceNumber } : {}),
            ...(invoiceDate ? { invoiceDate } : {}),
          },
          therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
        }).unwrap();
        invalidateAllFinanceTags(apt.patientId);
      }

      await updateAppointment({
        clinicId, appointmentId: apt.id,
        appointmentData: { isProducedReceipitForThisMeeting: true },
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();

      updateCardState(apt.id, { isLoading: false, currentStep: null });
      triggerCardCompletion(apt.id, () => {});
    } catch (e) { console.error("[CS-PA-016]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handleInvoiceYes = (apt) => {
    const cardState = getCardState(apt.id);
    setInvoiceAppointment({ ...apt, paymentMethod: cardState.paymentMethod ?? apt.paymentMethod ?? "cash" });
    setInvoicePaidAmount(cardState.paymentAmount ?? apt.actualPaidAmount);
    setShowInvoiceModal(true);
    updateCardState(apt.id, { currentStep: null });
  };

  const handleInvoiceNo = (apt) => updateCardState(apt.id, { currentStep: null });

  const handleInvoiceSuccess = async (invoiceData) => {
    const appointmentsToUpdate = invoiceData.appointments || (invoiceAppointment ? [invoiceAppointment] : []);
    for (const apt of appointmentsToUpdate) {
      try {
        await updateAppointment({
          clinicId, appointmentId: apt.id,
          appointmentData: {
            isProducedReceipitForThisMeeting: true,
            RecepitsDocsRefArray: [
              ...(apt.RecepitsDocsRefArray || []),
              { documentId: invoiceData.documentId, invoiceNumber: invoiceData.invoiceNumber, invoiceUrl: invoiceData.invoiceUrl, createdAt: new Date().toISOString() },
            ],
          },
          therapistId, therapistName, patientName: apt.patientName,
        }).unwrap();

        try {
          const transaction = await getTransactionByAppointment(clinicId, apt.id);
          if (transaction) {
            await updateTransactionStatus({
              clinicId, transactionId: transaction.id, newStatus: TRANSACTION_STATUS.INVOICE_AUTO,
              additionalData: { invoiceNumber: invoiceData.invoiceNumber, invoiceUrl: invoiceData.invoiceUrl, invoiceDocumentId: invoiceData.documentId },
              therapistId, therapistName, patientName: apt.patientName, patientId: apt.patientId,
            }).unwrap();
          }
        } catch (e) { console.error("[CS-PA-013b]", e); }
      } catch (e) { console.error("[CS-PA-013]", e); }
    }
  };

  const handleInvoiceModalClose = () => {
    setShowInvoiceModal(false);
    setInvoiceAppointment(null);
    setInvoicePaidAmount(null);
  };

  const handleSummaryYes = (apt) => {
    navigate(`/dashboard/patients/${apt.patientId}/treatments/new?appointmentId=${apt.id}`);
  };

  const handleSummaryNo = (apt) => updateCardState(apt.id, { currentStep: STEPS.SUMMARY_REMINDER });

  const handleSummaryReminderYes = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "summary" });
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 1);

    const appointmentDate = new Date(apt.startTime);
    const dateStr = appointmentDate.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
    const timeStr = appointmentDate.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

    try {
      await createTask({
        clinicId,
        taskData: {
          title: `לתעד סיכום פגישה עם ${apt.patientName} (${dateStr}, ${timeStr})`,
          description: `תזכורת לכתוב סיכום טיפול עבור הפגישה`,
          dueDate: dueDate.toISOString(), status: "todo", priority: "medium", scope: "personal",
          assignedTo: therapistId, relatedPatientId: apt.patientId, relatedAppointmentId: apt.id,
        },
        creatorId: therapistId, creatorName: therapistName,
      }).unwrap();

      await updateAppointment({
        clinicId, appointmentId: apt.id,
        appointmentData: { isTherapistSummarizedTheMeeting: true, NotifyAboutThisMeetingSummarize: false },
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();

      updateCardState(apt.id, { isLoading: false, currentStep: null });
    } catch (e) { console.error("[CS-PA-014]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handleSummaryReminderNo = async (apt) => {
    updateCardState(apt.id, { isLoading: true, loadingStage: "summary" });
    try {
      await updateAppointment({
        clinicId, appointmentId: apt.id,
        appointmentData: { isTherapistSummarizedTheMeeting: true, NotifyAboutThisMeetingSummarize: false },
        therapistId, therapistName, patientName: apt.patientName,
      }).unwrap();
      updateCardState(apt.id, { isLoading: false, currentStep: null });
    } catch (e) { console.error("[CS-PA-015]", e); updateCardState(apt.id, { isLoading: false }); }
  };

  const handleEndLiveSession = async (session) => {
    setEndingLiveSessions((prev) => ({ ...prev, [session.id]: true }));
    try {
      await endLiveSession({
        clinicId, sessionId: session.id,
        sessionData: { patientId: session.patientId, patientName: session.patientName },
        therapistId, therapistName,
      }).unwrap();
      setTimeout(() => {
        setEndingLiveSessions((prev) => { const next = { ...prev }; delete next[session.id]; return next; });
        dispatch(liveSessionsApi.util.invalidateTags([{ type: "ActiveLiveSession", id: therapistId }]));
        refetchPending();
      }, 800);
    } catch (e) {
      console.error("[CS-PA-016]", e);
      setEndingLiveSessions((prev) => { const next = { ...prev }; delete next[session.id]; return next; });
    }
  };

  const handleEnterLiveSession = (session) => {
    navigate(`/dashboard/live-session/${session.appointmentId}?patientId=${session.patientId}`);
  };

  // ==========================================
  // HANDLERS
  // ==========================================

  const handleSectionClick = useCallback((sectionId) => {
    // Always keep one section open — clicking the active section does nothing
    setActiveSection(prev => prev === sectionId ? prev : sectionId);
  }, []);

  const handleNotificationClick = (notification) => {
    if (notification.link) navigate(notification.link);
  };

  const handleMarkAsRead = async (e, notification) => {
    e.stopPropagation();
    try {
      await markAsRead({ clinicId, notificationId: notification.id, therapistId });
    } catch (error) {
      console.error("[CS-001] Error marking notification as read:", error);
    }
  };

  const handleHideReminder = (e, reminderId) => {
    e.stopPropagation();
    const newHiddenIds = [...hiddenReminderIds, reminderId];
    setHiddenReminderIds(newHiddenIds);
    try {
      localStorage.setItem(`clinikit_hidden_reminders_${therapistId}`, JSON.stringify(newHiddenIds));
    } catch (error) {
      console.error("[CS-002] Error saving hidden reminders:", error);
    }
  };

  const handleShowReminder = (e, reminderId) => {
    e.stopPropagation();
    const newHiddenIds = hiddenReminderIds.filter((id) => id !== reminderId);
    setHiddenReminderIds(newHiddenIds);
    try {
      localStorage.setItem(`clinikit_hidden_reminders_${therapistId}`, JSON.stringify(newHiddenIds));
    } catch (error) {
      console.error("[CS-003] Error saving hidden reminders:", error);
    }
  };

  // ==========================================
  // AUTO-COMPLETE EFFECT
  // ==========================================

  useEffect(() => {
    pendingAppointments.forEach((apt) => {
      const cardState = cardStates[apt.id] || {};
      const isAllDone =
        apt.isApprovedByTherapist === true &&
        apt.isTherapistUpdatedPaymentStatus === true &&
        apt.isTherapistSummarizedTheMeeting === true &&
        apt.NotifyAboutThisMeetingApproval !== true &&
        apt.NotifyAboutThisMeetingPaymentStatus !== true &&
        apt.NotifyAboutThisMeetingSummarize !== true &&
        !cardState.currentStep && !cardState.isLoading &&
        !completingCards[apt.id] && !allDoneShownRef.current.has(apt.id);

      if (isAllDone) {
        allDoneShownRef.current.add(apt.id);
        setTimeout(() => setCompletingCards((prev) => ({ ...prev, [apt.id]: true })), 1500);
      }
    });
  }, [pendingAppointments, cardStates, completingCards]);

  // Auto-expand when there is a single pending appointment (better UX)
  useEffect(() => {
    if (expandedItemId) return;
    const appointmentItems = pendingItems.filter((it) => it.type === "appointment");
    if (appointmentItems.length === 1) {
      setExpandedItemId(appointmentItems[0].data.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingItems]);

  // Collapse an item once it no longer appears in the pending list (e.g. after completion)
  useEffect(() => {
    if (!expandedItemId) return;
    const stillExists = pendingItems.some(
      (it) => it.type === "appointment" && it.data.id === expandedItemId
    );
    if (!stillExists) setExpandedItemId(null);
  }, [pendingItems, expandedItemId]);

  // ==========================================
  // RENDER PENDING ACTION CARD
  // ==========================================

  // Stage visibility / completion helpers for the pending actions list
  const getAppointmentStages = (apt) => {
    const stages = [];
    if (apt.NotifyAboutThisMeetingApproval === true) {
      stages.push({
        ...STAGE_CONFIG.approval,
        done: apt.isApprovedByTherapist === true,
      });
    }
    if (apt.NotifyAboutThisMeetingSummarize === true) {
      stages.push({
        ...STAGE_CONFIG.summary,
        done: apt.isTherapistSummarizedTheMeeting === true,
      });
    }
    if (canAccessFinance && apt.NotifyAboutThisMeetingPaymentStatus === true) {
      stages.push({
        ...STAGE_CONFIG.payment,
        done: apt.isTherapistUpdatedPaymentStatus === true,
      });
    }
    return stages.sort(
      (a, b) => STAGE_ORDER.indexOf(a.key) - STAGE_ORDER.indexOf(b.key)
    );
  };

  const getSubStepForStage = (stageKey, currentStep) => {
    const substeps = STAGE_SUBSTEPS[stageKey] || [];
    if (currentStep && substeps.includes(currentStep)) return currentStep;
    if (stageKey === "approval") return STEPS.APPROVAL;
    if (stageKey === "summary") return STEPS.SUMMARY;
    if (stageKey === "payment") return STEPS.PAYMENT;
    return null;
  };

  const renderStepContent = (apt, currentStep, cardState) => {
    if (cardState.isLoading) {
      return (
        <ContentLoader size="sm" compact message="מעדכן..." />
      );
    }

    if (currentStep === STEPS.APPROVAL) {
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">הפגישה התרחשה?</p>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleApprovalYes(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleApprovalNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.APPROVAL_CANCEL_CONFIRM) {
      return (
        <div className="cs-pa-step-content cs-pa-warning">
          <Warning size={20} weight="fill" className="cs-pa-warning-icon" />
          <p className="cs-pa-question">לבטל את הפגישה?</p>
          <div className="cs-pa-text-btns">
            <button className="cs-pa-text-btn cs-pa-text-btn-danger" onClick={(e) => { e.stopPropagation(); handleApprovalCancelConfirm(apt); }}>אני בטוח</button>
            <button className="cs-pa-text-btn" onClick={(e) => { e.stopPropagation(); handleApprovalCancelBack(apt); }}>חזרה</button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.CANCELLATION_PAYMENT_ASK) {
      return (
        <div className="cs-pa-step-content">
          <CurrencyCircleDollar size={20} weight="duotone" className="cs-pa-icon-blue" />
          <p className="cs-pa-question">לתעד דמי ביטול?</p>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleCancellationPaymentYes(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleCancellationPaymentNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.CANCELLATION_PAYMENT_AMOUNT) {
      const cancelAmount = cardState.paymentAmount ?? "";
      const hasCancelAmount = cancelAmount && parseFloat(cancelAmount) > 0;
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">סכום דמי הביטול:</p>
          <div className="cs-pa-payment-row">
            <span className="cs-pa-currency">₪</span>
            <input type="number" className="cs-pa-input" placeholder="סכום" value={cardState.paymentAmount ?? ""} onClick={(e) => e.stopPropagation()}
              onChange={(e) => updateCardState(apt.id, { paymentAmount: e.target.value })} />
          </div>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" disabled={!hasCancelAmount} onClick={(e) => { e.stopPropagation(); handleCancellationPaymentAmountConfirm(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleCancellationPaymentNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.CANCELLATION_PAYMENT_DETAILS) {
      return (
        <div className="cs-pa-step-content cs-pa-payment-details">
          <p className="cs-pa-question">פרטי התשלום</p>
          <div className="cs-pa-form">
            <div className="cs-pa-form-row">
              <label>תאריך</label>
              <DatePicker className="cs-pa-date-input" value={cardState.paymentDate || ""}
                onChange={(e) => updateCardState(apt.id, { paymentDate: e.target.value })} />
            </div>
            <div className="cs-pa-form-row">
              <label>אמצעי תשלום</label>
              <div className="cs-pa-methods">
                {PAYMENT_METHODS.map((m) => (
                  <button key={m.value} className={`cs-pa-method ${cardState.paymentMethod === m.value ? "active" : ""}`}
                    onClick={(e) => { e.stopPropagation(); updateCardState(apt.id, { paymentMethod: m.value }); }}>{m.label}{m.subLabel && <span style={{ fontSize: '0.55em', opacity: 0.6, display: 'block', lineHeight: 1.1 }}>{m.subLabel}</span>}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleCancellationPaymentDetailsConfirm(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleCancellationPaymentNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.CANCELLATION_PAYMENT_INVOICE) {
      return (
        <div className="cs-pa-step-content">
          <Receipt size={20} weight="duotone" className="cs-pa-icon-orange" />
          <p className="cs-pa-question">להפיק חשבונית?</p>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleCancellationInvoiceYes(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleCancellationInvoiceNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.PAYMENT) {
      const effectiveAmount = cardState.paymentAmount ?? apt.paymentAmount;
      const hasValidAmount = effectiveAmount && parseFloat(effectiveAmount) > 0;
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">התשלום טופל?</p>
          <div className="cs-pa-payment-row">
            <span className="cs-pa-currency">₪</span>
            <input type="number" className="cs-pa-input" placeholder={apt.paymentAmount || "סכום"} value={cardState.paymentAmount ?? ""} onClick={(e) => e.stopPropagation()}
              onChange={(e) => updateCardState(apt.id, { paymentAmount: e.target.value })} />
          </div>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" disabled={!hasValidAmount} onClick={(e) => {
              e.stopPropagation();
              const appointmentDate = new Date(apt.startTime);
              updateCardState(apt.id, { currentStep: STEPS.PAYMENT_DETAILS, paymentAmount: cardState.paymentAmount ?? apt.paymentAmount,
                paymentDate: getIsraelDateString(appointmentDate), paymentMethod: "cash" });
            }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handlePaymentNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.PAYMENT_DETAILS) {
      return (
        <div className="cs-pa-step-content cs-pa-payment-details">
          <p className="cs-pa-question">פרטי התשלום</p>
          <div className="cs-pa-form">
            <div className="cs-pa-form-row">
              <label>תאריך קבלה</label>
              <DatePicker className="cs-pa-date-input" value={cardState.paymentDate || ""}
                onChange={(e) => updateCardState(apt.id, { paymentDate: e.target.value })} />
            </div>
            <div className="cs-pa-form-row">
              <label>אמצעי תשלום</label>
              <div className="cs-pa-methods">
                {PAYMENT_METHODS.map((m) => (
                  <button key={m.value} className={`cs-pa-method ${cardState.paymentMethod === m.value ? "active" : ""}`}
                    onClick={(e) => { e.stopPropagation(); updateCardState(apt.id, { paymentMethod: m.value }); }}>{m.label}{m.subLabel && <span style={{ fontSize: '0.55em', opacity: 0.6, display: 'block', lineHeight: 1.1 }}>{m.subLabel}</span>}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handlePaymentYes(apt, cardState.paymentAmount, cardState.paymentMethod, cardState.paymentDate); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); updateCardState(apt.id, { currentStep: null }); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.PAYMENT_TASK) {
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">להוסיף תזכורת גבייה?</p>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handlePaymentTaskYes(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handlePaymentTaskNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.PAYMENT_INVOICE) {
      return (
        <div className="cs-pa-step-content">
          <Receipt size={20} weight="duotone" className="cs-pa-icon-orange" />
          <p className="cs-pa-question">להפיק חשבונית?</p>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleReceiptYes(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleInvoiceNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.PAYMENT_INVOICE_CHOICE) {
      return (
        <div className="cs-pa-step-content">
          <Receipt size={20} weight="duotone" className="cs-pa-icon-orange" />
          <p className="cs-pa-question">איך להפיק את החשבונית?</p>
          <div className="cs-pa-text-btns">
            <button className="cs-pa-text-btn cs-pa-text-btn-primary" onClick={(e) => { e.stopPropagation(); handleInvoiceYes(apt); }}>הפקה אוטומטית</button>
            <button className="cs-pa-text-btn" onClick={(e) => { e.stopPropagation(); handleInvoiceChoiceManual(apt); }}>סימון ידני</button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.PAYMENT_INVOICE_MANUAL) {
      return (
        <div className="cs-pa-step-content cs-pa-payment-details">
          <p className="cs-pa-question">פרטי החשבונית (לא חובה)</p>
          <div className="cs-pa-form">
            <div className="cs-pa-form-row">
              <label>מספר חשבונית</label>
              <input type="text" className="cs-pa-input" placeholder="לדוגמה: 1042" value={cardState.invoiceNumber ?? ""} onClick={(e) => e.stopPropagation()}
                onChange={(e) => updateCardState(apt.id, { invoiceNumber: e.target.value })} />
            </div>
            <div className="cs-pa-form-row">
              <label>תאריך הפקת החשבונית</label>
              <DatePicker className="cs-pa-date-input" value={cardState.invoiceDate || ""}
                onChange={(e) => updateCardState(apt.id, { invoiceDate: e.target.value })} />
            </div>
          </div>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleManualInvoiceConfirm(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleInvoiceNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.SUMMARY) {
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">להוסיף סיכום פגישה?</p>
          <div className="cs-pa-action-btns">
            {canWriteTreatments !== false && (
              <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleSummaryYes(apt); }}><Check size={16} weight="bold" /></button>
            )}
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleSummaryNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    if (currentStep === STEPS.SUMMARY_REMINDER) {
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">להוסיף תזכורת לסיכום?</p>
          <div className="cs-pa-action-btns">
            <button className="cs-pa-btn cs-pa-btn-confirm" onClick={(e) => { e.stopPropagation(); handleSummaryReminderYes(apt); }}><Check size={16} weight="bold" /></button>
            <button className="cs-pa-btn cs-pa-btn-cancel" onClick={(e) => { e.stopPropagation(); handleSummaryReminderNo(apt); }}><X size={16} weight="bold" /></button>
          </div>
        </div>
      );
    }

    return (
      <div className="cs-pa-step-content cs-pa-all-done">
        <CheckCircle size={28} weight="fill" className="cs-pa-done-icon" />
        <p>הושלם!</p>
      </div>
    );
  };

  const renderStagePanel = (apt, stage, cardState) => {
    const { Icon, title } = stage;
    const isLoadingThisStage =
      cardState.isLoading && cardState.loadingStage === stage.key;

    if (stage.done) {
      return (
        <div key={stage.key} className="cs-pa-stage cs-pa-stage-done" data-stage={stage.key}>
          <div className="cs-pa-stage-header">
            <div className="cs-pa-stage-title">
              <Icon size={15} weight="fill" />
              <span>{title}</span>
            </div>
            <span className="cs-pa-stage-badge done">
              <Check size={10} weight="bold" />
              הושלם
            </span>
          </div>
        </div>
      );
    }

    const subStep = getSubStepForStage(stage.key, cardState.currentStep);
    const effectiveState = isLoadingThisStage
      ? cardState
      : { ...cardState, isLoading: false };

    return (
      <div
        key={stage.key}
        className="cs-pa-stage cs-pa-stage-pending"
        data-stage={stage.key}
      >
        <div className="cs-pa-stage-header">
          <div className="cs-pa-stage-title">
            <Icon size={15} weight="fill" />
            <span>{title}</span>
          </div>
          <span className="cs-pa-stage-badge pending">נדרש עדכון</span>
        </div>
        <div className="cs-pa-stage-body" key={subStep || "initial"}>
          {renderStepContent(apt, subStep, effectiveState)}
        </div>
      </div>
    );
  };

  const renderPendingItem = (apt) => {
    const cardState = getCardState(apt.id);
    const isCompleting = completingCards[apt.id] === true;
    const isExpanded = expandedItemId === apt.id;
    const stages = getAppointmentStages(apt);
    const pendingStages = stages.filter((s) => !s.done);

    const toggleExpand = () =>
      setExpandedItemId((prev) => (prev === apt.id ? null : apt.id));

    return (
      <div
        key={apt.id}
        className={`cs-pa-item ${isCompleting ? "completing" : ""} ${
          isExpanded ? "expanded" : ""
        } ${pendingStages.length > 0 ? "has-pending" : ""}`}
      >
        <button
          type="button"
          className="cs-pa-item-header"
          onClick={toggleExpand}
          aria-expanded={isExpanded}
        >
          <div className="cs-pa-item-info">
            <span className="cs-pa-item-name">{apt.patientName}</span>
            <span className="cs-pa-item-time">
              {formatDate(apt.startTime)} • {formatTime(apt.startTime)}
            </span>
          </div>
          <div className="cs-pa-item-meta">
            <div className="cs-pa-chips" aria-hidden={false}>
              {stages.map((s) => {
                const StageIcon = s.Icon;
                return (
                  <span
                    key={s.key}
                    className={`cs-pa-chip ${s.done ? "done" : "pending"}`}
                    data-stage={s.key}
                    title={s.done ? `${s.title} — הושלם` : `${s.title} — נדרש`}
                  >
                    {s.done ? (
                      <Check size={10} weight="bold" />
                    ) : (
                      <StageIcon size={10} weight="fill" />
                    )}
                    <span className="cs-pa-chip-label">{s.shortTitle}</span>
                  </span>
                );
              })}
            </div>
            <span
              className={`cs-pa-item-chevron ${isExpanded ? "rotated" : ""}`}
              aria-hidden="true"
            >
              <CaretDown size={13} weight="bold" />
            </span>
          </div>
        </button>

        {isExpanded && (
          <div className="cs-pa-item-body">
            {stages.map((s) => renderStagePanel(apt, s, cardState))}
          </div>
        )}
      </div>
    );
  };

  const renderLiveSessionItem = (session) => {
    const isEnding = endingLiveSessions[session.id] === true;
    return (
      <div
        key={`live-${session.id}`}
        className={`cs-pa-item cs-pa-item-live ${isEnding ? "ending" : ""}`}
      >
        <div className="cs-pa-item-header cs-pa-item-header-live">
          <div className="cs-pa-item-info">
            <span className="cs-pa-live-pill">
              <span className="cs-pa-live-dot" />
              סשן לייב פעיל
            </span>
            <span className="cs-pa-item-name">{session.patientName}</span>
            <span className="cs-pa-item-time">
              {formatDate(session.appointmentStartTime || session.startedAt)} •{" "}
              {formatTime(session.appointmentStartTime || session.startedAt)}
            </span>
          </div>
          <div className="cs-pa-live-actions">
            <button
              type="button"
              className="cs-pa-btn-play"
              onClick={() => handleEnterLiveSession(session)}
              title="המשך סשן"
              aria-label="המשך סשן"
            >
              <Play size={15} weight="fill" />
            </button>
            <button
              type="button"
              className="cs-pa-btn-stop"
              onClick={() => handleEndLiveSession(session)}
              disabled={isEnding}
              title="סיום סשן"
              aria-label="סיום סשן"
            >
              {isEnding ? (
                <LoaderLoop size="xs" />
              ) : (
                <Stop size={15} weight="bold" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // RENDER SECTION CONTENT
  // ==========================================

  const renderPendingActionsContent = () =>
    isNutritionClinic ? (
      <div className="cs-section-content cs-pending-content">
        {/* The therapy demo teaches the 3-stage appointment update, which a
            nutrition coach does not have — they get their own sample item. */}
        {demoEnabled && demoActive ? (
          <NutritionPendingDemo onClose={closeDemo} />
        ) : (
          <NutritionPendingSection
            onStartDemo={demoEnabled && !demoSeen ? startDemo : null}
          />
        )}
      </div>
    ) : (
    <div className="cs-section-content cs-pending-content">
      {demoEnabled && demoActive ? (
        <div className="cs-pa-list" role="list">
          <PendingActionsDemo onClose={closeDemo} />
        </div>
      ) : (isLoadingPending || isLoadingLive) && pendingItems.length === 0 ? (
        <ContentLoader message="טוען..." />
      ) : pendingItems.length === 0 ? (
        <div className="cs-empty-state cs-empty-success">
          <EmptyDraw variant="done" />
          <span>אין פעולות ממתינות</span>
          <span className="cs-empty-subtitle">כל הפגישות מעודכנות</span>
          {demoEnabled && !demoSeen && (
            <button type="button" className="pad-cta" onClick={startDemo}>
              <PlayCircle size={16} weight="fill" />
              <span>רוצה לראות איך זה עובד? פגישה לדוגמה</span>
            </button>
          )}
        </div>
      ) : (
        <div className={`cs-pa-list ${expandedItemId ? "cs-pa-list-focused" : ""}`} role="list">
          {pendingItems.map((item) =>
            item.type === "live"
              ? renderLiveSessionItem(item.data)
              : renderPendingItem(item.data)
          )}
        </div>
      )}
    </div>
  );

  const renderNotificationsContent = () => (
    <div className="cs-section-content">
      {notifications.length === 0 ? (
        <div className="cs-empty-state">
          <EmptyDraw variant="bell" />
          <span>אין התראות חדשות</span>
        </div>
      ) : (
        <div className="cs-list">
          {notifications.slice(0, showMoreNotifications ? 15 : 5).map((notification) => (
            <div key={notification.id} className={`cs-notification-item ${getUrgencyClass(notification.urgency)}`} onClick={() => handleNotificationClick(notification)}>
              <div className="cs-notification-dot" />
              <div className="cs-notification-content">
                <span className="cs-notification-title">{notification.title}</span>
                <span className="cs-notification-message">{notification.message}</span>
              </div>
              <div className="cs-notification-meta">
                <span className="cs-notification-time">{formatRelativeTime(notification.createdAt)}</span>
                <button className="cs-icon-btn" onClick={(e) => handleMarkAsRead(e, notification)} title="סמן כנצפה">
                  <Eye size={14} weight="regular" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {notifications.length > 5 && (
        <div className="cs-section-footer">
          <button className="cs-view-all-btn" onClick={() => setShowMoreNotifications((v) => !v)}>
            <span>
              {showMoreNotifications
                ? "הצג פחות"
                : `הצג עוד (${Math.min(notifications.length, 15) - 5})`}
            </span>
            <CaretDown size={14} weight="bold" className={`cs-footer-caret ${showMoreNotifications ? "rotated" : ""}`} />
          </button>
        </div>
      )}
    </div>
  );

  const renderCalendarContent = () => (
    <div className="cs-section-content cs-calendar-content">
      <div className="cs-calendar-sections">
        <div className="cs-calendar-block">
          <div className="cs-calendar-block-header">
            <span className="cs-label">היום</span>
            <span className="cs-count">{sortedTodayAppointments.length}</span>
          </div>
          {sortedTodayAppointments.length === 0 ? (
            <div className="cs-empty-inline">
              <EmptyDraw variant="calendar" size="sm" />
              <span>אין פגישות היום</span>
            </div>
          ) : (
            <div className="cs-apt-list">
              {sortedTodayAppointments.slice(0, 5).map((apt) => {
                const confirmStatus = getConfirmationStatus(apt);
                const amount = formatAmount(apt.paymentAmount);
                return (
                  <div key={apt.id} className={`cs-apt-card cs-apt-${confirmStatus.type}`} onClick={() => onEditAppointment && onEditAppointment(apt)}>
                    <div className="cs-apt-main">
                      <span className="cs-apt-time">{formatTime(apt.startTime)}</span>
                      <span className="cs-apt-name">{apt.patientName}</span>
                    </div>
                    <div className="cs-apt-meta">
                      {amount && <span className="cs-apt-amount">{amount}</span>}
                      <span className={`cs-apt-status cs-apt-status-${confirmStatus.type}`}>
                        {confirmStatus.type === "confirmed" && <CheckCircle size={10} weight="fill" />}
                        {confirmStatus.type === "pending" && <ClockCountdown size={10} weight="fill" />}
                        {(confirmStatus.type === "declined" || confirmStatus.type === "cancelled") && <X size={10} weight="bold" />}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {groupedUpcoming.length > 0 && (
          <div className="cs-calendar-block cs-upcoming">
            <div className="cs-calendar-block-header">
              <Calendar size={14} weight="fill" />
              <span className="cs-label">בהמשך השבוע</span>
            </div>
            <div className="cs-grouped-list">
              {groupedUpcoming.slice(0, 2).map((group) => (
                <div key={group.dateLabel} className="cs-day-group">
                  <span className="cs-day-label">{group.dateLabel}</span>
                  {group.appointments.slice(0, 2).map((apt) => {
                    const confirmStatus = getConfirmationStatus(apt);
                    return (
                      <div key={apt.id} className={`cs-apt-mini cs-apt-${confirmStatus.type}`} onClick={() => onEditAppointment && onEditAppointment(apt)}>
                        <span className="cs-apt-time">{formatTime(apt.startTime)}</span>
                        <span className="cs-apt-name">{apt.patientName}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="cs-section-footer">
        <button className="cs-view-all-btn" onClick={() => navigate("/dashboard/calendar")}>
          <span>לצפייה ביומן המלא</span>
          <ArrowRight size={14} weight="bold" className="cs-arrow-rtl" />
        </button>
      </div>
    </div>
  );

  const renderTasksContent = () => (
    <div className="cs-section-content">
      {allTasks.length === 0 ? (
        <div className="cs-empty-state cs-empty-success">
          <EmptyDraw variant="done" />
          <span>אין משימות פתוחות</span>
        </div>
      ) : (
        <div className="cs-list">
          {allTasks.slice(0, 5).map((task) => {
            const statusConfig = getTaskStatusConfig(task.status);
            return (
              <div key={task.id} className={`cs-task-item ${task.status === "done" ? "cs-completed" : ""}`}>
                <div className="cs-task-dot" style={{ backgroundColor: statusConfig.color }} />
                <span className="cs-task-title">{task.title}</span>
                <span className="cs-task-status" style={{ color: statusConfig.color }}>{statusConfig.label}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className="cs-section-footer">
        <button className="cs-view-all-btn" onClick={() => navigate("/dashboard/tasks")}>
          <span>{allTasks.length > 5 ? `לכל המשימות (${allTasks.length})` : "לכל המשימות"}</span>
          <ArrowRight size={14} weight="bold" className="cs-arrow-rtl" />
        </button>
      </div>
    </div>
  );

  const renderRemindersContent = () => (
    <div className="cs-section-content">
      {allReminders.length === 0 ? (
        <div className="cs-empty-state">
          <EmptyDraw variant="reminder" size="sm" />
          <span>אין תזכורות</span>
        </div>
      ) : visibleReminders.length === 0 ? (
        <div className="cs-empty-state">
          <EmptyDraw variant="hidden" />
          <span>כל התזכורות מוסתרות</span>
        </div>
      ) : (
        <div className="cs-list">
          {visibleReminders.slice(0, 5).map((reminder) => {
            const isHidden = hiddenReminderIds.includes(reminder.id);
            const plainContent = reminder.content ? reminder.content.replace(/<[^>]*>/g, "").trim() : "";
            const contentPreview = plainContent.length > 60 ? plainContent.substring(0, 60) + "..." : plainContent;
            return (
              <div key={reminder.id} className={`cs-reminder-item ${isHidden ? "cs-hidden" : ""}`}>
                <div className="cs-reminder-content">
                  <span className="cs-reminder-title">{reminder.title}</span>
                  {contentPreview && <span className="cs-reminder-text">{contentPreview}</span>}
                </div>
                <button className="cs-icon-btn" onClick={(e) => isHidden ? handleShowReminder(e, reminder.id) : handleHideReminder(e, reminder.id)} title={isHidden ? "הצג" : "הסתר"}>
                  {isHidden ? <Eye size={14} /> : <EyeSlash size={14} />}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="cs-section-footer">
        {hiddenRemindersCount > 0 && (
          <button className="cs-toggle-hidden-btn" onClick={(e) => { e.stopPropagation(); setShowHiddenReminders(!showHiddenReminders); }}>
            {showHiddenReminders ? <EyeSlash size={14} /> : <Eye size={14} />}
            <span>{hiddenRemindersCount} מוסתרות</span>
          </button>
        )}
        <button className="cs-view-all-btn" onClick={() => navigate("/dashboard/tasks?tab=reminders")}>
          <span>{allReminders.length > 5 ? `לכל התזכורות (${allReminders.length})` : "לכל התזכורות"}</span>
          <ArrowRight size={14} weight="bold" className="cs-arrow-rtl" />
        </button>
      </div>
    </div>
  );

  const renderLeadsContent = () => (
    <div className="cs-section-content">
      <div className="cs-section-header-actions">
        <button className="cs-add-btn" onClick={(e) => { e.stopPropagation(); navigate("/dashboard/tasks?tab=leads&action=new"); }} title="הוסף מתעניין">
          <Plus size={14} weight="bold" />
          <span>מתעניין חדש</span>
        </button>
      </div>
      {activeLeads.length === 0 ? (
        <div className="cs-empty-state">
          <EmptyDraw variant="lead" size="sm" />
          <span>אין מתעניינים פעילים</span>
        </div>
      ) : (
        <div className="cs-list">
          {activeLeads.slice(0, 5).map((lead) => {
            const statusConfig = getLeadStatusConfig(lead.status);
            return (
              <div key={lead.id} className="cs-lead-item">
                <div className="cs-lead-dot" style={{ backgroundColor: statusConfig?.color || "#3b82f6" }} />
                <span className="cs-lead-name">{lead.name}</span>
                <span className="cs-lead-status" style={{ color: statusConfig?.color || "#3b82f6" }}>{statusConfig?.label || "חדש"}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className="cs-section-footer">
        <button className="cs-view-all-btn" onClick={() => navigate("/dashboard/tasks?tab=leads")}>
          <span>{activeLeads.length > 5 ? `לכל המתעניינים (${activeLeads.length})` : "לכל המתעניינים"}</span>
          <ArrowRight size={14} weight="bold" className="cs-arrow-rtl" />
        </button>
      </div>
    </div>
  );

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case "pendingActions": return renderPendingActionsContent();
      case "notifications": return renderNotificationsContent();
      case "calendar": return renderCalendarContent();
      case "tasks": return renderTasksContent();
      case "reminders": return renderRemindersContent();
      case "leads": return renderLeadsContent();
      default: return null;
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  const renderDesktop = () => (
    <div className="cs-rail" role="tablist" aria-label="סקירת קליניקה">
      {sectionsConfig.map((section) => {
        const Icon = section.icon;
        const isActive = activeSection === section.id;
        const badgeCount = sectionBadges[section.id];
        const isUrgent = section.id === "pendingActions" && badgeCount > 0;
        // Active strip hosts the HelpButton, so it must not be a <button> itself
        const StripTag = isActive ? "div" : "button";

        return (
          <div key={section.id} className={`cs-tab ${isActive ? "cs-tab-active" : "cs-tab-collapsed"}`} data-section-id={section.id}>
            <StripTag
              {...(isActive ? { tabIndex: 0 } : { type: "button" })}
              role="tab"
              aria-selected={isActive}
              className="cs-tab-strip"
              onClick={() => handleSectionClick(section.id)}
            >
              <span className={`cs-tab-icon-chip ${isActive ? "cs-tab-icon-chip-active" : ""}`}>
                <Icon size={20} weight={isActive ? "fill" : "regular"} />
                {!isActive && badgeCount > 0 && (
                  <span className={`cs-tab-badge ${isUrgent ? "cs-tab-badge-urgent" : ""}`}>{badgeCount > 9 ? "9+" : badgeCount}</span>
                )}
              </span>
              <span className="cs-tab-label">{isActive ? section.label : section.shortLabel}</span>
              {isActive && badgeCount > 0 && <span className="cs-tab-count-pill">{badgeCount}</span>}
              {isActive && section.id === "pendingActions" && (
                <span className="cs-tab-help">
                  <button
                    type="button"
                    className="cs-tab-focus-btn"
                    title="תצוגה ייעודית"
                    aria-label="תצוגה ייעודית"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFocusMode(true);
                    }}
                  >
                    <ArrowsOutSimple size={14} weight="bold" />
                  </button>
                  {renderDemoTrigger()}
                  <HelpButton featureKey="pending_actions" />
                </span>
              )}
            </StripTag>
            {isActive && (
              <div className="cs-tab-panel" role="tabpanel">
                {renderSectionContent(section.id)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderMobile = () => {
    const activeConfig = sectionsConfig.find((s) => s.id === activeSection) || sectionsConfig[0];
    const ActiveIcon = activeConfig.icon;
    const activeBadge = sectionBadges[activeConfig.id];

    return (
      <div className="cs-mobile">
        <div className="cs-chip-row" role="tablist" aria-label="סקירת קליניקה">
          {sectionsConfig.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            const badgeCount = sectionBadges[section.id];
            const isUrgent = section.id === "pendingActions" && badgeCount > 0;
            return (
              <button
                key={section.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                ref={(el) => { chipRefs.current[section.id] = el; }}
                className={`cs-chip ${isActive ? "cs-chip-active" : ""}`}
                onClick={() => handleSectionClick(section.id)}
              >
                <Icon size={16} weight={isActive ? "fill" : "regular"} />
                <span>{section.shortLabel}</span>
                {badgeCount > 0 && (
                  <span className={`cs-chip-count ${isUrgent ? "cs-chip-count-urgent" : ""}`}>{badgeCount > 9 ? "9+" : badgeCount}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="cs-mobile-panel" role="tabpanel" key={activeConfig.id}>
          <div className="cs-mobile-panel-header">
            <span className="cs-tab-icon-chip cs-tab-icon-chip-active">
              <ActiveIcon size={18} weight="fill" />
            </span>
            <span className="cs-mobile-panel-title">{activeConfig.label}</span>
            {activeBadge > 0 && <span className="cs-tab-count-pill">{activeBadge}</span>}
            {activeConfig.id === "pendingActions" && (
              <span className="cs-tab-help">
                {renderDemoTrigger()}
                <HelpButton featureKey="pending_actions" />
              </span>
            )}
          </div>
          {renderSectionContent(activeConfig.id)}
        </div>
      </div>
    );
  };

  return (
    <div className="cs-container">
      {isMobile ? renderMobile() : renderDesktop()}

      {showFocusMode && !isMobile && (
        <PendingFocusMode
          items={pendingItems}
          formatDate={formatDate}
          formatTime={formatTime}
          renderStagePanel={renderStagePanel}
          getAppointmentStages={getAppointmentStages}
          getCardState={getCardState}
          completingCards={completingCards}
          endingLiveSessions={endingLiveSessions}
          handleEnterLiveSession={handleEnterLiveSession}
          handleEndLiveSession={handleEndLiveSession}
          isLoading={isLoadingPending || isLoadingLive}
          onClose={() => setShowFocusMode(false)}
        />
      )}

      {isDigitalInvoiceActive ? (
        <DigitalInvoiceQuickModal
          isOpen={showInvoiceModal}
          onClose={handleInvoiceModalClose}
          appointment={invoiceAppointment}
          patientName={invoiceAppointment?.patientName}
          amount={invoicePaidAmount}
          paymentMethod={invoiceAppointment?.paymentMethod}
          onSuccess={handleInvoiceSuccess}
        />
      ) : (
        <InvoiceQuickModal
          isOpen={showInvoiceModal}
          onClose={handleInvoiceModalClose}
          appointment={invoiceAppointment}
          patientName={invoiceAppointment?.patientName}
          amount={invoicePaidAmount}
          paymentMethod={invoiceAppointment?.paymentMethod}
          onSuccess={handleInvoiceSuccess}
        />
      )}
    </div>
  );
}

export default CollapsibleSections;
